/*
 * Terms of Service — public page at /terms.
 *
 * NOTE FOR THE OWNER: this text was drafted by an AI assistant on 2026-09-27
 * from what the product actually does (checked against the code at the time).
 * It is NOT legal advice and should be reviewed by a solicitor before the
 * first paying customer. Points that need a human decision or a fact check
 * (company number, registered office, liability cap floor, sub-processor
 * notice period, VAT wording) were listed in the hand-over report.
 *
 * Layout, TOC, print styles and the link/table primitives are shared with the
 * Privacy Policy — see the exports at the top of PrivacyPolicyPage.js.
 */
import React from 'react';
import usePageMeta from '../../utils/pageMeta';
import { LegalPage, P, Bullets, InlineLink } from './PrivacyPolicyPage';

const CONTACT = <InlineLink href="mailto:support@nesthub.ie">support@nesthub.ie</InlineLink>;

const TERMS_SECTIONS = [
  {
    id: 'who-we-are',
    title: 'Who we are',
    body: (
      <>
        <P>NestHub is provided by Linehan O'Brien Investments Ltd ("NestHub", "we", "us"), a company registered in Ireland, based in Dublin. You can contact us at {CONTACT}.</P>
        <P>These terms apply when you create a NestHub account or use the service. By creating an account you agree to them on behalf of yourself and, if you sign up for a business, on behalf of that business ("you").</P>
      </>
    ),
  },
  {
    id: 'the-service',
    title: 'The service',
    body: (
      <>
        <P>NestHub is online software that helps landlords and letting agents manage properties, tenants, rent, deposits, documents, maintenance and compliance deadlines. Features differ by plan, as shown on our <InlineLink to="/pricing">pricing page</InlineLink>. Features marked "Beta" are still being developed and may change or be withdrawn.</P>
      </>
    ),
  },
  {
    id: 'not-advice',
    title: 'Not legal, tax or financial advice',
    body: (
      <>
        <P>NestHub is a tool, not an adviser. Nothing in NestHub is legal, tax or financial advice.</P>
        <Bullets items={[
          'You remain responsible for meeting your obligations as a landlord, including registering tenancies with the Residential Tenancies Board (RTB), serving valid notices, following rent pressure zone and rent cap rules, and filing with the Revenue Commissioners.',
          'Our calculators (for example rent cap and notice-period calculators), reminders and document templates are guidance based on our understanding of the rules at the time. They may not cover your situation. Where the RTB publishes its own calculator or guidance, the RTB\'s version prevails.',
          'Check anything important with the RTB, Revenue or a qualified professional before you rely on it.',
        ]} />
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Accounts and eligibility',
    body: (
      <>
        <Bullets items={[
          'NestHub is for business use by landlords, letting agents and property managers. You must be at least 18 to open an account.',
          'Tenants can use NestHub only by invitation from their landlord. A tenant\'s use of the tenant portal is also governed by these terms, but the landlord is our customer.',
          'You must give accurate information, keep your password secure, and tell us promptly at support@nesthub.ie if you think someone else has accessed your account. We recommend turning on two-factor authentication in Settings.',
          'You are responsible for what happens under your account, including the actions of staff you invite.',
        ]} />
      </>
    ),
  },
  {
    id: 'trial-and-plans',
    title: 'Free trial, plans and payment',
    body: (
      <>
        <Bullets items={[
          'New accounts start with a 14-day free trial. No payment card is needed to start it, and we will not charge you automatically when it ends. To keep using NestHub after the trial you need to choose a paid plan.',
          <>Paid plans are monthly subscriptions, billed in advance through our payment provider, Stripe. Current prices are shown on our <InlineLink to="/pricing">pricing page</InlineLink>.</>,
          'Some plans have limits (for example on the number of properties). If you go over a limit, we may ask you to move to a larger plan or charge any per-property amount shown on the pricing page.',
          'We may change our prices. We will give you at least 30 days\' notice by email before a price change affects your subscription, and you can cancel before it takes effect.',
          'If a payment fails, we will let you know. If it is still unpaid after a reasonable period, we may restrict your account to read-only access or suspend it until payment is made.',
        ]} />
      </>
    ),
  },
  {
    id: 'cancellation',
    title: 'Cancellation and refunds',
    body: (
      <>
        <P>You can cancel at any time in Settings → Manage subscription. Cancellation takes effect at the end of your current billing period, and you keep access until then.</P>
        <P>We do not give refunds for part-months or unused time, except where the law requires it. Before you close your account, you can download your data using "Export My Data" in Settings.</P>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    body: (
      <>
        <P>You must not:</P>
        <Bullets items={[
          'use NestHub for anything unlawful, including discrimination against tenants or applicants, harassment, or unlawful eviction',
          'upload data you have no right to use, or content that is defamatory, abusive or infringes someone else\'s rights',
          'send spam or unsolicited messages through NestHub',
          'try to access another organisation\'s data, probe or break our security, or interfere with the service',
          'copy, resell or reverse-engineer the software, or scrape it with automated tools, except as the law allows',
          'share your login with people outside your organisation',
        ]} />
        <P>If you breach these rules we may suspend or close your account (see <InlineLink href="#termination">Suspension and termination</InlineLink>).</P>
      </>
    ),
  },
  {
    id: 'your-data',
    title: 'Your data',
    body: (
      <>
        <P>You own the data you put into NestHub ("customer data"). You give us permission to host, copy and process it only as needed to provide the service to you, to keep it secure, and to fix problems.</P>
        <P>For personal data about your tenants and other people, you are the data controller and we are your data processor. You are responsible for having a lawful basis to collect that data and put it into NestHub, and for giving the people concerned any privacy information the law requires. Our <InlineLink to="/privacy">Privacy Policy</InlineLink> explains how we handle personal data, and the next section sets out our commitments as your processor.</P>
      </>
    ),
  },
  {
    id: 'data-processing',
    title: 'Data processing (Article 28 GDPR)',
    body: (
      <>
        <P>This section is our data processing agreement with you. It applies to personal data we process on your behalf as your processor.</P>
        <Bullets items={[
          <><strong>Subject matter and duration:</strong> providing NestHub to you, for as long as your account is open and until the data is deleted under this section.</>,
          <><strong>Nature and purpose:</strong> hosting, storing, organising, displaying and transmitting data so that you can manage your lettings; sending emails and notices you trigger; generating documents; matching bank payments where you connect a bank.</>,
          <><strong>Types of data:</strong> names, contact details, property and tenancy details, rent, deposit and payment records, bank transaction details for matched payments, maintenance reports and photos, documents, signatures and messages.</>,
          <><strong>Data subjects:</strong> your tenants, licensees, applicants, guarantors, people paying rent on a tenant's behalf, contractors and your own staff.</>,
          <><strong>Instructions:</strong> we process the data only on your documented instructions — which are these terms and the way you use and configure NestHub — unless EU or Irish law requires otherwise, in which case we will tell you first where the law allows.</>,
          <><strong>Confidentiality:</strong> everyone we authorise to process the data is bound by confidentiality.</>,
          <><strong>Security:</strong> we maintain appropriate technical and organisational measures, including those described in section 12 of our <InlineLink to="/privacy#security">Privacy Policy</InlineLink>.</>,
          <><strong>Sub-processors:</strong> you authorise us to use the sub-processors listed in our <InlineLink to="/privacy#sub-processors">Privacy Policy</InlineLink>. We impose data protection obligations on each of them equivalent to these, and we remain responsible for them. We will email you before adding or replacing a sub-processor; if you object on reasonable data protection grounds and we cannot resolve it, you may cancel your subscription.</>,
          <><strong>Data subject requests:</strong> taking into account the nature of the processing, we will help you respond to requests from people exercising their GDPR rights, and pass on to you any request we receive directly.</>,
          <><strong>Other assistance:</strong> we will give you reasonable help with security, breach notification, data protection impact assessments and consultation with the Data Protection Commission, based on the information available to us.</>,
          <><strong>Breach notification:</strong> we will notify you without undue delay after becoming aware of a personal data breach affecting your data, with the information you need to meet your own obligations.</>,
          <><strong>Deletion or return:</strong> you can export your data at any time using "Export My Data" in Settings. When your account is closed, we delete customer data within 90 days, unless the law requires us to keep it.</>,
          <><strong>Audits:</strong> we will make available the information reasonably needed to show that we meet these obligations, and allow and contribute to reasonable audits on reasonable written notice.</>,
          <><strong>International transfers:</strong> where a sub-processor processes data outside the European Economic Area, we make sure an approved safeguard such as the Standard Contractual Clauses is in place.</>,
        ]} />
      </>
    ),
  },
  {
    id: 'bank-connection',
    title: 'Bank connection',
    body: (
      <>
        <P>If your plan includes it, you can connect a bank account so NestHub can match incoming payments to rent. This feature is optional.</P>
        <Bullets items={[
          'The connection is provided by Enable Banking Oy, a Finnish company regulated as an account information service provider. You give your consent to them and to your bank, not to NestHub.',
          'Access is read-only. NestHub never sees your online banking credentials and cannot make payments or move money.',
          'Transactions are fetched only to match rent — automatically once a day, and whenever you press Sync. We do not keep them beyond the rent payments that are matched and recorded, and the proposed matches waiting for your review (see our Privacy Policy for details).',
          'Bank consents last up to 180 days; after that you need to reconnect.',
          'You can withdraw consent at any time by choosing Disconnect in NestHub\'s Bank Matching page, or directly with your bank.',
          'Automatic matching can be wrong. Please review matched payments; you remain responsible for your rent records.',
        ]} />
      </>
    ),
  },
  {
    id: 'rent-collection',
    title: 'Rent collection',
    body: (
      <>
        <P>Where it is available, you can give each tenant their own account number (IBAN) to pay rent into, so that payments are recorded automatically. This feature is optional.</P>
        <Bullets items={[
          'The payment service is provided by Stripe, not by NestHub. You open your own Stripe account and agree to Stripe\'s terms directly with Stripe. Rent is paid into your Stripe account and paid out by Stripe to your bank account. NestHub never receives, holds or controls the money.',
          'NestHub is software. With your authorisation it does the following on your Stripe account: creates a customer record and an account number for each tenant, reads incoming bank transfers, applies each transfer as a payment, and reads the status of your account. It reads the payer\'s name, the last four digits of their IBAN, the amount, the date and the payment reference in order to record the payment.',
          'You can remove NestHub\'s access at any time from your Stripe dashboard. Payments will then no longer be recorded automatically.',
          'Stripe charges its own fee for each bank transfer, shown on Stripe\'s pricing page and in NestHub before you sign up. NestHub does not add a fee and does not take a share of the rent. The NestHub subscription is charged separately.',
          'Bank transfers cannot be reversed. Any refund to a tenant is a matter between you and your tenant.',
          'A transfer that cannot be matched to a tenant may be returned to the sender by Stripe.',
          'The feature is for rent. It must not be used to hold a deposit or any other money on behalf of a tenant, or to collect money on behalf of another property owner.',
          'Automatic recording can be wrong. You remain responsible for your rent records.',
        ]} />
      </>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and changes to the service',
    body: (
      <>
        <P>We use reasonable efforts to keep NestHub available and working properly, but we do not guarantee any particular uptime, and the service may sometimes be unavailable — for example during maintenance, updates, or problems with our providers or your internet connection. Where we can, we will do planned maintenance at quiet times.</P>
        <P>We keep improving NestHub and may change or remove features. If we remove a major feature you pay for, we will tell you in advance and you may cancel.</P>
      </>
    ),
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual property',
    body: (
      <P>We (or our licensors) own NestHub, including the software, design, templates and content we provide. We give you a non-exclusive, non-transferable right to use it for your own business while your account is active. Documents you generate from our templates are yours to use for your tenancies. If you send us feedback or suggestions, we may use them without any obligation to you.</P>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    body: (
      <>
        <P>To the extent permitted by Irish law:</P>
        <Bullets items={[
          'NestHub is provided "as is". We do not promise that it will be error-free, uninterrupted or suitable for every purpose, and we exclude implied terms except those that cannot be excluded.',
          'We are not liable for indirect or consequential loss, or for loss of profit, rent, revenue, business, goodwill or data (you should keep your own copies of important records, for example using Export My Data).',
          'We are not liable for losses caused by relying on NestHub instead of checking your legal or tax obligations, for example a notice or rent review that turns out to be invalid.',
          'Our total liability to you for all claims arising out of or in connection with NestHub in any 12-month period is limited to the fees you paid us in the 12 months before the event giving rise to the claim.',
        ]} />
        <P>Nothing in these terms limits or excludes liability for death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or any other liability that cannot be limited or excluded under Irish law. If you are a consumer, nothing in these terms affects your statutory rights.</P>
      </>
    ),
  },
  {
    id: 'termination',
    title: 'Suspension and termination',
    body: (
      <>
        <P>You can stop using NestHub and cancel at any time (see <InlineLink href="#cancellation">Cancellation and refunds</InlineLink>). To close your account completely, use "Request Account Deletion" in Settings or email us.</P>
        <P>We may suspend or close your account if you seriously or repeatedly breach these terms, if payment is overdue, or if we must do so by law. Where reasonable, we will warn you first and give you a chance to put things right. We may also stop offering NestHub altogether, giving you at least 60 days' notice.</P>
        <P>When your account ends, your right to use NestHub stops, and we handle your data as described in the <InlineLink href="#data-processing">Data processing</InlineLink> section. Sections that by their nature should survive — including your data, liability, and governing law — continue to apply.</P>
      </>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    body: (
      <P>We may update these terms, for example to reflect new features or changes in the law. We will email you about material changes at least 30 days before they take effect. If you do not agree, you can cancel before then; if you keep using NestHub after the changes take effect, the new terms apply. The date at the top of this page shows when the terms were last revised.</P>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing law',
    body: (
      <P>These terms are governed by the laws of Ireland, and the courts of Ireland have jurisdiction over any dispute arising from them. If you are a consumer living elsewhere in the EU, you may also have the right to bring proceedings in your own country.</P>
    ),
  },
  {
    id: 'general',
    title: 'General',
    body: (
      <Bullets items={[
        'These terms, together with our Privacy Policy, are the whole agreement between you and us about NestHub.',
        'If any part of these terms is found to be unenforceable, the rest stays in effect.',
        'If we do not enforce a right straight away, we can still enforce it later.',
        'You may not transfer your account or these terms without our consent. We may transfer them to another company that takes over NestHub, and we will tell you if that happens.',
        'We are not responsible for delays or failures caused by events outside our reasonable control.',
      ]} />
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <Bullets items={[
        <>Email: {CONTACT}</>,
        <>Post: Linehan O'Brien Investments Ltd, Dublin, Ireland</>,
      ]} />
    ),
  },
];

export default function TermsPage() {
  usePageMeta({
    title: 'Terms of Service | NestHub',
    description: 'The terms for using NestHub property management software in Ireland: free trial, plans and cancellation, data processing, bank connection and liability.',
    canonical: '/terms',
  });

  return (
    <LegalPage
      title="Terms of Service"
      lastUpdated="27 September 2026"
      intro={<P sx={{ mb: 0 }}>Please read these terms carefully. They explain what you can expect from NestHub and what we expect from you. Our <InlineLink to="/privacy">Privacy Policy</InlineLink> explains how we handle personal data.</P>}
      sections={TERMS_SECTIONS}
    />
  );
}
